import { useEffect } from 'react';
import './confirm-modal.css';

/**
 * Props del ConfirmModal
 */
export interface ConfirmModalProps {
  /** Controla si el modal está abierto */
  isOpen: boolean;
  /** Callback al cerrar el modal */
  onClose: () => void;
  /** Callback al confirmar la acción */
  onConfirm: () => void;
  /** Título del modal */
  title: string;
  /** Mensaje descriptivo */
  message: string;
  /** Texto del botón de confirmar (default: "Confirmar") */
  confirmText?: string;
  /** Texto del botón de cancelar (default: "Cancelar") */
  cancelText?: string;
  /** Tipo de acción (afecta el color del botón de confirmar) */
  variant?: 'danger' | 'warning' | 'info';
  /** Indica si la acción está en progreso */
  isLoading?: boolean;
}

/**
 * Modal de confirmación reutilizable con diseño WESTEER
 *
 * @remarks
 * - Glassmorphism con dark theme
 * - Backdrop blur oscuro
 * - Animaciones suaves
 * - Cierre con ESC
 * - Cierre al hacer clic fuera del modal
 * - Bloqueo de scroll cuando está abierto
 *
 * @example
 * ```tsx
 * const [showConfirm, setShowConfirm] = useState(false);
 *
 * <ConfirmModal
 *   isOpen={showConfirm}
 *   onClose={() => setShowConfirm(false)}
 *   onConfirm={handleDelete}
 *   title="Eliminar Usuario"
 *   message="¿Estás seguro de eliminar a juan@example.com? Esta acción no se puede deshacer."
 *   confirmText="Eliminar"
 *   cancelText="Cancelar"
 *   variant="danger"
 * />
 * ```
 */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'danger',
  isLoading = false,
}: ConfirmModalProps): JSX.Element | null {
  /**
   * Bloquear scroll del body cuando el modal está abierto
   */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  /**
   * Cerrar con tecla ESC
   */
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  /**
   * Manejar confirmación
   */
  const handleConfirm = () => {
    onConfirm();
    // No cerrar automáticamente, dejar que el componente padre lo maneje
  };

  if (!isOpen) return null;

  return (
    <div className="confirm-modal-overlay" onClick={onClose}>
      <div
        className="confirm-modal-container"
        onClick={(e) => e.stopPropagation()} // Prevenir cierre al hacer clic dentro
      >
        {/* Icono según variante */}
        <div className={`confirm-modal-icon confirm-modal-icon-${variant}`}>
          {variant === 'danger' && (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0377 2.66667 10.2679 4L3.33975 16C2.56995 17.3333 3.53223 19 5.07183 19Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          {variant === 'warning' && (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          {variant === 'info' && (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 16H12V12H11M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>

        {/* Título */}
        <h2 className="confirm-modal-title">{title}</h2>

        {/* Mensaje */}
        <p className="confirm-modal-message">{message}</p>

        {/* Botones */}
        <div className="confirm-modal-actions">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="confirm-modal-btn confirm-modal-btn-cancel"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            className={`confirm-modal-btn confirm-modal-btn-confirm confirm-modal-btn-${variant}`}
          >
            {isLoading ? 'Procesando...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
